/**
 * Reopening a closed request period.
 *
 * A period takes requests while nobody has closed it and today is on or
 * before its due date. So clearing a manual close is not enough to reopen a
 * period whose due date has passed: it would still be closed. Reopening
 * therefore always comes with a due date, from today on and before the
 * period starts (the same rule the period was opened under). Once the period
 * has started there is no such date, and the admin enters any late change on
 * the availability grid instead.
 *
 * Reopening never touches the requests: everything submitted, approved or
 * declined stays exactly as it was. Pure, so the admin's screen and the
 * server run the same check.
 */
import { addDays, compareDates, type ISODate } from '../dates/iso.ts';

/**
 * Why a period cannot be reopened with a given due date:
 * - `started`: the period's first week has begun, so no due date can work;
 * - `due-date-past`: the due date is before today, so it would still be closed;
 * - `due-date-not-before-start`: the due date is on or after the first day.
 */
export type ReopenProblem = 'started' | 'due-date-past' | 'due-date-not-before-start';

export function reopenProblem(startDate: ISODate, dueDate: ISODate, today: ISODate): ReopenProblem | null {
  if (compareDates(today, startDate) >= 0) return 'started';
  if (compareDates(dueDate, today) < 0) return 'due-date-past';
  if (compareDates(dueDate, startDate) >= 0) return 'due-date-not-before-start';
  return null;
}

/** How long physicians get by default when a period past its due date is reopened. */
export const REOPEN_DEFAULT_DAYS = 7;

/**
 * The due date to offer when the admin reopens a period: its own due date
 * if that is still to come, otherwise a week from today, but never on or
 * after the day the period starts. Null once the period has started.
 */
export function suggestedReopenDueDate(startDate: ISODate, dueDate: ISODate, today: ISODate): ISODate | null {
  if (compareDates(today, startDate) >= 0) return null;
  if (reopenProblem(startDate, dueDate, today) === null) return dueDate;
  const inAWeek = addDays(today, REOPEN_DEFAULT_DAYS);
  const lastDay = addDays(startDate, -1);
  return compareDates(inAWeek, lastDay) <= 0 ? inAWeek : lastDay;
}
